import React, { useEffect, useState } from "react";
import {
    Paper,
    Skeleton,
    Typography,
    Box,
    Divider,
    Button,
    Grid,
} from "@mui/material";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import BusinessIcon from '@mui/icons-material/Business';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import DescriptionIcon from '@mui/icons-material/Description';
import { useAgreementsController } from "./context/agreements-controller";
import { useAgreementsState } from "./context/agreements-context";
import PageHeader from "../components/shared/page-header";
import { useParams } from "react-router-dom";
import { useNavigate } from "../../hooks/useNavigate";
import dayjs from "dayjs";

export function AgreementDetailComponent() {
    const [initialLoading, setInitialLoading] = useState(true);
    const { loadDetail } = useAgreementsController();
    const { agreementDetail } = useAgreementsState();
    const { id } = useParams();
    const navigate = useNavigate();

    useEffect(() => {
        const fetchData = async () => {
            if (id) {
                await loadDetail(Number(id));
                setInitialLoading(false);
            }
        };
        fetchData();
    }, [id]);

    const handleBack = () => {
        navigate('/convenios-repasses');
    };

    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        }).format(value);
    };

    return (
        <Box>
            <PageHeader
                title={agreementDetail?.superiorBody || 'Carregando...'}
                subtitle=""
                breadcrumbs={[
                    { label: 'Convênios e Repasses', href: '/convenios-repasses' },
                    { label: agreementDetail?.superiorBody || 'Carregando...' }
                ]}
            />

            {initialLoading ? (
                <Box>
                    <Skeleton variant="rounded" width="100%" height={400} sx={{ borderRadius: 2, mb: 3 }} />
                    <Skeleton variant="text" width="60%" height={60} sx={{ mb: 2 }} />
                    <Skeleton variant="text" width="100%" height={30} />
                    <Skeleton variant="text" width="100%" height={30} />
                    <Skeleton variant="text" width="80%" height={30} />
                </Box>
            ) : (
                <React.Fragment>
                    <Paper sx={{ p: 4, mb: 3 }} variant="outlined">
                        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                            Informações do Convênio
                        </Typography>

                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                    <AccountBalanceIcon color="primary" />
                                    <Box>
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            Órgão Superior
                                        </Typography>
                                        <Typography variant="body1" fontWeight={500}>
                                            {agreementDetail?.superiorBody || '-'}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                    <AccountBalanceIcon color="primary" />
                                    <Box>
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            Órgão Subordinado
                                        </Typography>
                                        <Typography variant="body1" fontWeight={500}>
                                            {agreementDetail?.subordinateBody || '-'}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                    <BusinessIcon color="primary" />
                                    <Box>
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            Unidade Gestora
                                        </Typography>
                                        <Typography variant="body1" fontWeight={500}>
                                            {agreementDetail?.managementUnit || '-'}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                    <CalendarTodayIcon color="primary" />
                                    <Box>
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            Período de Vigência
                                        </Typography>
                                        <Typography variant="body1" fontWeight={500}>
                                            {agreementDetail?.startDate && agreementDetail?.endDate
                                                ? `${dayjs(agreementDetail.startDate).format('DD/MM/YYYY')} - ${dayjs(agreementDetail.endDate).format('DD/MM/YYYY')}`
                                                : '-'}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Grid>

                            {agreementDetail?.agreementNumber && (
                                <Grid item xs={12} md={6}>
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                        <DescriptionIcon color="primary" />
                                        <Box>
                                            <Typography variant="caption" color="text.secondary" display="block">
                                                Número do Convênio
                                            </Typography>
                                            <Typography variant="body1" fontWeight={500}>
                                                {agreementDetail.agreementNumber}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Grid>
                            )}
                        </Grid>

                        <Divider sx={{ my: 4 }} />

                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                            Valores
                        </Typography>

                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <Paper
                                    sx={{
                                        p: 3,
                                        backgroundColor: 'primary.50',
                                        border: '2px solid',
                                        borderColor: 'primary.main',
                                    }}
                                    elevation={0}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                        <AttachMoneyIcon color="primary" fontSize="large" />
                                        <Box>
                                            <Typography variant="caption" color="text.secondary" display="block">
                                                Valor do Repasse
                                            </Typography>
                                            <Typography variant="h5" fontWeight={600} color="brand.text">
                                                {formatCurrency(agreementDetail?.transferAmount || 0)}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Paper>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Paper
                                    sx={{
                                        p: 3,
                                        backgroundColor: 'success.50',
                                        border: '2px solid',
                                        borderColor: 'success.main',
                                    }}
                                    elevation={0}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                        <AttachMoneyIcon color="success" fontSize="large" />
                                        <Box>
                                            <Typography variant="caption" color="text.secondary" display="block">
                                                Valor da Contrapartida
                                            </Typography>
                                            <Typography variant="h5" fontWeight={600} color="success.main">
                                                {formatCurrency(agreementDetail?.counterpartAmount || 0)}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Paper>
                            </Grid>
                        </Grid>

                        {agreementDetail?.description && (
                            <Box sx={{ mt: 4 }}>
                                <Divider sx={{ mb: 3 }} />
                                <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                                    Descrição
                                </Typography>
                                <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                                    {agreementDetail.description}
                                </Typography>
                            </Box>
                        )}
                    </Paper>

                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <Button
                            variant="contained"
                            startIcon={<ArrowBackIcon />}
                            onClick={handleBack}
                            size="large"
                        >
                            Voltar para Convênios e Repasses
                        </Button>
                    </Box>
                </React.Fragment>
            )}
        </Box>
    );
}
